<div class="popover-container" [class.bottom-sheet]="bottomSheet" [class.centered]="centered"
  (click)="onContainerClick($event)">
  <!-- Header: on the phone bottom sheet, and on a centred dialog, which without its title would
       open in the middle of the screen with nothing saying what it is -->
  @if ((bottomSheet || centered) && (title || backButton || activeSubmenu)) {
    <div class="header">
      @if (backButton || activeSubmenu) {
        <cs-icon name="arrow-back" (click)="handleBackClick()"></cs-icon>
      }
      <span class="header-title">{{title}}</span>
      <!-- a real button, in the app's own style, so an action that changes several things at once
           reads as one rather than as a quiet link -->
      @if (headerAction) {
        <cs-button small secondary icon="refresh" class="header-action" data-e2e="menu-header-action"
          (click)="handleHeaderActionClick()">{{headerAction.text}}</cs-button>
      }
      <!-- a centred dialog is closed by its own × , the way every other dialog in the app is
           (cs-alert). The bottom sheet is dragged or tapped away and needs none. -->
      @if (centered) {
        <cs-icon name="close" [ngClass]="'header-close'" (click)="close()"></cs-icon>
      }
    </div>
  }

  <!-- Mobile bottom sheet: single dropdown with animated content switching -->
  @if (bottomSheet) {
    <div class="dropdown-menu" [class.showing-submenu]="activeSubmenu">
      <!-- Main menu content -->
      <div class="menu-content main-content" [class.active]="!activeSubmenu">
        <ng-container *ngTemplateOutlet="mainMenuContent"></ng-container>
      </div>
      <!-- Submenu content -->
      <div class="menu-content submenu-content" [class.active]="activeSubmenu">
        @if (activeSubmenu) {
          @for (o of activeSubmenu.options; track o.value) {
            <div class="menu-item"
              [class.selected]="!selectMultiple && o.value === value">
              <button cs-capture-cover (click)="selectOption(o, $event)"></button>
              <span>{{ o.name || o.description }}</span>
              @if (o.submenu) {
                <cs-icon name="arrow-dropright" class="trailing-icon"></cs-icon>
              }
            </div>
          }
        }
      </div>
    </div>
  }

  <!-- Desktop: show main menu and submenu side-by-side -->
  @if (!bottomSheet) {
    <div class="menus-container">
      <!-- Main dropdown menu (always visible on desktop) -->
      <div class="dropdown-menu">
        <ng-container *ngTemplateOutlet="mainMenuContent"></ng-container>
      </div>
      <!-- Submenu panel (positioned to the right, visible when activeSubmenu is set) -->
      @if (activeSubmenu) {
        <div class="submenu-panel" (mouseleave)="onSubmenuMouseLeave()">
          <div class="dropdown-menu">
            @for (o of activeSubmenu.options; track o.value) {
              <div class="menu-item"
                [class.selected]="!selectMultiple && o.value === value">
                <button cs-capture-cover (click)="selectOption(o, $event)"></button>
                <span>{{ o.name || o.description }}</span>
                @if (o.submenu) {
                  <cs-icon name="arrow-dropright" class="trailing-icon"></cs-icon>
                }
              </div>
            }
          </div>
        </div>
      }
    </div>
  }
</div>

<!-- Shared template for main menu content -->
<ng-template #mainMenuContent>
  <!-- search input -->
  @if (searchable) {
    <cs-input iconName="search" autocomplete="off" [(value)]="searchQuery"
      type="text" inputmode="search" [placeholder]="title"
      (input)="onSearchInput(searchQuery)" />
  }

  <!-- one loop for both inputs: a flat `options` list arrives here as one section with no header -->
  @for (subsection of shownSections; track $index) {
    <!-- subsection header -->
    @if (subsection.header) {
      <div class="subsection-header">{{ subsection.header }}</div>
    }
    <!-- radio options wrapped in cs-radio-group -->
    @if (hasRadioOptions(subsection)) {
      <cs-radio-group [selected]="getRadioValue(subsection)"
        (selectedChange)="onRadioChange(subsection, $event)"
        menu>
        @for (o of subsection.options; track o.value) {
          @if (o.controlType === 'radio') {
            <cs-radio [value]="o.value">
              {{ o.name }}
            </cs-radio>
          }
        }
      </cs-radio-group>
    }
    <!-- non-radio options (toggle and checkbox) -->
    @if (!hasRadioOptions(subsection)) {
      @for (o of subsection.options; track o.value) {
        <!-- toggle option - text on left, toggle on right -->
        @if (o.controlType === 'toggle') {
          <div class="menu-item toggle-item" (click)="selectOption(o)">
            <!-- a toggle that carries BOTH a name and a description shows the description as a
                 quiet line under the name, because a switch whose wording cannot say everything
                 (Hide Unverified, #5549) needs to explain what it covers where it is switched -->
            <span class="toggle-text">
              <span>{{ o.name || o.description }}</span>
              @if (o.name && o.description) {
                <span class="toggle-hint">{{ o.description }}</span>
              }
            </span>
            <cs-toggle [checked]="o.selectState" (checkedChange)="onToggleChange(o, $event)"></cs-toggle>
          </div>
        }
        <!-- checkbox option (default) -->
        @if (!o.controlType || o.controlType === 'checkbox') {
          <div class="menu-item"
            [class.selected]="!selectMultiple && o.value === value"
            [class.submenu-active]="!bottomSheet && activeSubmenu && o.submenu === activeSubmenu"
            [class.disabled]="o.disabled"
            (mouseenter)="onOptionHover(o)"
            (mouseleave)="onOptionMouseLeave(o)">
            <button cs-capture-cover [disabled]="o.disabled" (click)="selectOption(o, $event)"></button>
            @if (selectMultiple) {
              <div class="item-icon">
                <!-- [ariaLabel] names the option: the row's own capture-cover button means
                     there is no row text a checkbox can take its name from -->
                <cs-checkbox [ariaLabel]="o.name" [checked]="o.selectState" [disabled]="o.disabled" (checkedChange)="selectOption(o)"></cs-checkbox>
              </div>
            }
            @if (showIcons()) {
              <div class="item-icon">
                <cs-icon [name]="o.icon"></cs-icon>
              </div>
            }
            <span>{{ o.name || o.description }}</span>
            <ng-container *ngTemplateOutlet="o.contentTemplate"></ng-container>
            @if (o.submenu) {
              <cs-icon name="arrow-dropright" class="trailing-icon"></cs-icon>
            }
            @if (o.helpIcon) {
              <cs-tooltip [text]="o.description" [iconType]="'help'" class="help-icon"></cs-tooltip>
            }
          </div>
        }
      }
    }
  }
</ng-template>
